import { CubePresenter, Logger } from 'wdc-cube';
import { ConceptScope } from '../../../generic/concept.scope';
import { CollaboratorsKeys } from './collaborators.key';
import { collaboratorTableModel } from './collaborator-table';
import { Places } from '../../../route-consts';
const LOG = Logger.get('Mormaii.CollaboratorsPresenter');
/**
 * O CONCEITO "colaboradores", que nao e uma tela.
 *
 * Sao duas telas - a listagem e o detalhe -, e este place e o pai comum das duas.
 * Ele nao desenha: quem para aqui e reencaminhado para a listagem, que e a
 * composicao padrao do assunto. Ver `ConceptScope` para o porque do desenho.
 *
 * ## O que ele guarda
 *
 * O filtro, a ordenacao e a pagina. Sao do ASSUNTO e nao da listagem: o detalhe os
 * carrega para que o "Voltar" caia na mesma lista filtrada, e e por isso que eles
 * precisam sobreviver enquanto o detalhe esta aberto - momento em que a listagem
 * nem existe.
 *
 * Antes deste place, o dono era a listagem, e ela tinha de continuar viva por baixo
 * do detalhe so para nao perder isso. O preco era um desvio em `applyParameters`
 * que cada lista escrevia a mao, e que tinha uma sutileza nao obvia: nao desenhar
 * nem buscar, mas AINDA assim atualizar o estado, senao o primeiro write apagava os
 * filtros do endereco.
 */
export class CollaboratorsPresenter extends CubePresenter {
    constructor(app) {
        super(app, new ConceptScope());
        /**
         * O estado comum, mantido aqui e em nenhum outro lugar.
         *
         * Nasce dos parametros a cada viagem e volta para eles em `publishParameters` -
         * este presenter esta no caminho das DUAS telas, entao o valor nunca se perde.
         */
        this.at = { criteria: {}, page: 1 };
        /**
         * O modelo da tabela da listagem: quais medicoes sao coluna e em que ordem (B-42), a
         * largura de cada uma, as presas e a ordem das linhas. Mora no CONCEITO, e nao na
         * listagem, porque a listagem e liberada quando o detalhe abre (sao irmaos): voltar
         * a ela mediria a tabela de novo, com um quadro piscando no layout automatico. A
         * escolha gravada antes dele, nas chaves de cada guarda antiga, e lida na primeira vez.
         */
        this.table = collaboratorTableModel('collaborators', () => this.app.currentSession?.id, {
            columns: 'mormaii.collaboratorColumns',
            widths: 'mormaii.columnWidths:collaborators'
        });
    }
    async applyParameters(intent, initialization, last) {
        const keys = new CollaboratorsKeys(this.app, intent);
        /*
         * A guarda de papel NAO sobe para ca, e isso e deliberado.
         *
         * Ela e da LISTAGEM: o profissional nao tem a lista de colaboradores no
         * menu, mas abre o DETALHE por link direto - um endereco que um admin
         * compartilhou -, e na referencia `/collaborators/:id` esta aberto a
         * qualquer perfil do hub. Guardar o conceito inteiro transformaria esse
         * link num redirecionamento.
         */
        keys.concept = this;
        this.owner = keys.owner;
        // Sem empresa no endereco, a PADRAO de quem esta logado: quem administra mais
        // de uma precisa dizer qual, e o servidor recusa agir sem saber.
        const empresa = keys.companyId ?? keys.owner?.companyInScope;
        /*
         * Trocar de empresa volta para a PRIMEIRA pagina.
         *
         * A pagina 7 da empresa anterior pode nao existir na nova, e a lista abriria
         * vazia sem dizer por que. A pagina viaja no endereco e sobrevive a viagem que
         * a troca de empresa provoca, entao quem a zera e quem percebe a troca.
         */
        const trocouDeEmpresa = this.at.criteria.companyId !== undefined && this.at.criteria.companyId !== empresa;
        this.at = {
            criteria: {
                name: keys.name,
                cpf: keys.cpf,
                email: keys.email,
                companyId: empresa,
                measurementTypeId: keys.measurementTypeId,
                measurementStatusLevel: keys.statusLevel,
                activeFrom: keys.activeFrom,
                activeTo: keys.activeTo,
                activityKind: keys.activityKind,
                activityPresence: keys.activityPresence,
                registeredFrom: keys.registeredFrom,
                registeredTo: keys.registeredTo,
                statusFrom: keys.statusFrom,
                statusTo: keys.statusTo,
                activeAccountsOnly: keys.activeAccountsOnly ? true : undefined,
                anamnese: keys.anamnese
            },
            sort: keys.sort,
            page: trocouDeEmpresa ? 1 : keys.page
        };
        if (initialization) {
            LOG.info('Initialized');
        }
        if (last) {
            /*
             * Ninguem pediu tela nenhuma, so o assunto.
             *
             * Parar aqui nao e um estado: nao ha o que desenhar. O `false`
             * interrompe a caminhada, e o redirecionamento leva a composicao
             * padrao do assunto - a listagem.
             *
             * `intent.redirect` e nao `Keys.of(app)`: o `of` monta o intent
             * PERGUNTANDO aos presenters vivos, e durante este proprio flip este
             * presenter ainda nao foi comitado - a aplicacao ainda responde com os
             * anteriores. O filtro que acabou de chegar se perderia. O `redirect`
             * copia parametro e atributo do intent que esta em maos, que e onde
             * eles estao.
             */
            await this.app.flipToIntent(intent.redirect(Places.collaboratorList));
            return false;
        }
        return true;
    }
    publishParameters(intent) {
        const keys = new CollaboratorsKeys(this.app, intent);
        keys.name = this.at.criteria.name;
        keys.cpf = this.at.criteria.cpf;
        keys.email = this.at.criteria.email;
        keys.companyId = this.owner?.companyIdInAddress(this.at.criteria.companyId);
        keys.measurementTypeId = this.at.criteria.measurementTypeId;
        keys.activeFrom = this.at.criteria.activeFrom;
        keys.activeTo = this.at.criteria.activeTo;
        keys.activityKind = this.at.criteria.activityKind;
        keys.activityPresence = this.at.criteria.activityPresence;
        keys.registeredFrom = this.at.criteria.registeredFrom;
        keys.registeredTo = this.at.criteria.registeredTo;
        keys.statusFrom = this.at.criteria.statusFrom;
        keys.statusTo = this.at.criteria.statusTo;
        keys.activeAccountsOnly = !!this.at.criteria.activeAccountsOnly;
        keys.anamnese = this.at.criteria.anamnese;
        keys.statusLevel = this.at.criteria.measurementStatusLevel;
        keys.sort = this.at.sort;
        keys.page = this.at.page;
    }
    /** O que a listagem e o detalhe estao mostrando. */
    get where() {
        return this.at;
    }
    /**
     * A listagem mexeu no filtro, na ordem ou na pagina.
     *
     * Ela trabalha sobre uma copia - e dela que sai a assinatura da consulta -, mas
     * quem devolve isso ao endereco e este presenter. Sem esta chamada antes do
     * `updateHistory()`, ordenar mudaria a tela e nao a URL.
     */
    moveTo(where) {
        this.at = where;
    }
}
//# sourceMappingURL=collaborators.presenter.js.map